import React, { useEffect } from 'react';

interface MetricChartTileProps {
  data: any[];
  setError?: (error: string) => void;
  initialized?: boolean;
}

const MIN_TILE_WIDTH = 140; // Pixels: minimum width per metric cell before wrapping

function formatMetricValue(val: any): string {
  if (typeof val !== 'number' || isNaN(val)) return String(val);
  const absVal = Math.abs(val);
  let formatted: string;
  let suffix = '';

  if (absVal >= 1e9) {
    formatted = (val / 1e9).toFixed(2);
    suffix = 'B';
  } else if (absVal >= 1e6) {
    formatted = (val / 1e6).toFixed(2);
    suffix = 'M';
  } else if (absVal >= 1e3) {
    formatted = (val / 1e3).toFixed(2);
    suffix = 'K';
  } else {
    formatted = val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }

  // Keep at most three digits in front of the decimal, strip leading zeros, and always two decimals if abbreviated
  if (suffix) {
    const [intPart, decPart] = formatted.split('.');
    const trimmedInt = intPart.length > 3 ? intPart.slice(0, 3) : intPart;
    formatted = decPart ? `${trimmedInt}.${decPart}` : trimmedInt;
    return formatted + suffix;
  }
  return formatted;
}

const MetricChartTile: React.FC<MetricChartTileProps> = ({
  data,
  setError,
  initialized = false
}) => {
  useEffect(() => {
    if (initialized && !Array.isArray(data)) {
      console.error("Expected data to be an array, but got:", data);
      if (setError) {
        setError("Invalid data format");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, initialized]);
      
  if (!setError && initialized && !Array.isArray(data)) {
    return <div>Invalid data format</div>;
  }

  return (
    <div style={{ width: '100%', height: '100%', paddingTop: 12 }}>
      <ul
        className="metric-chart-container"
        style={{
          // Use CSS Grid to auto-fit columns based on parent width.
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${MIN_TILE_WIDTH}px, 1fr))`,
          gridAutoFlow: 'row',
          gap: '12px 16px',
          alignItems: 'stretch',
          justifyItems: 'center',
          listStyleType: 'none',
          padding: 0,
          margin: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {data.map((item: any, index: number) => (
          <li
            key={index}
            style={{
              display: 'grid',
              placeItems: 'center',
              textAlign: 'center',
              padding: '8px 4px',
              minWidth: 0,
            }}
          >
            <span className="hbr-css__text-primary-sm-bold" style={{ fontSize: '2rem', lineHeight: 1.25 }}>
              {formatMetricValue(item.value)}
            </span>
            <span className="hbr-css__text-secondary-xs-semibold" style={{ marginTop: 4 }}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MetricChartTile;